import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadModal from "@/components/LeadModal";

// ── Data constants ─────────────────────────────────────────────────────────────

const FOUNDERS = [
  {
    name: "Dr. Evan Viswas",
    title: "Identity, Research & Strategy",
    initials: "EV",
    color: "#C9A84C",
    imageUrl: "https://customer-assets.emergentagent.com/job_soulprint-travel/artifacts/tz8e7ky4_Dr.%20Evan%20Viswas.jpeg",
    pillars: [
      "PhD in Tourism Studies",
      "Identity Driven Travel research",
      "Soulprint creator",
      "Experience psychology",
      "Strategic direction",
      "Experience architecture",
    ],
  },
  {
    name: "Mr. Mohamed Dawoudine",
    title: "Operations & Execution",
    initials: "MD",
    color: "#06B6D4",
    imageUrl: "https://customer-assets.emergentagent.com/job_soulprint-travel/artifacts/csxm1oib_Mr.%20Mohamed%20Dawoudine.jpeg",
    pillars: [
      "Travel operations",
      "Supplier management",
      "Departure execution",
      "Customer delivery",
      "Commercial systems",
      "Ground operations",
    ],
  },
];

const EVOLUTION_STEPS = [
  "IDENTITY",
  "TRAVEL BEHAVIOUR",
  "TRAVEL IDENTITY",
  "IDENTITY DRIVEN TRAVEL",
  "SOULPRINT FRAMEWORK",
  "TRAVEL DNA THEORY",
  "IDENTITY DRIVEN TOURISM",
  "TRAVELORZA ECOSYSTEM",
];

const TRANSFORMATION_MODEL = [
  "WHO YOU ARE",
  "WHAT YOU NEED",
  "WHAT YOU EXPERIENCE",
  "WHO YOU BECOME",
];

const ECOSYSTEM_NODES = [
  { label: "IDENTITY",            angle: 0   },
  { label: "SOULPRINT",           angle: 40  },
  { label: "TRAVEL DNA",          angle: 80  },
  { label: "ORZA",                angle: 120 },
  { label: "EXPERIENCE DESIGN",   angle: 160 },
  { label: "CURATED JOURNEYS",    angle: 200 },
  { label: "TRANSFORMATION",      angle: 240 },
  { label: "ARCHETYPE EVOLUTION", angle: 280 },
  { label: "NEW SOULPRINT",       angle: 320 },
];

const INTELLECTUAL_PILLARS = [
  "PhD-led research",
  "Identity Driven Travel",
  "Identity Driven Tourism",
  "Soulprint Framework",
  "Travel DNA Theory",
  "Traveler transformation research",
];

const OPERATIONAL_PILLARS = [
  "Experience architecture",
  "Curated departures",
  "Supplier ecosystem",
  "Hospitality expertise",
  "Tour operations capability",
  "Commercial execution",
];

const VALIDATION_FLOW = [
  "IDEA",
  "REAL TRAVELERS",
  "REAL JOURNEYS",
  "REAL REVENUE",
  "VALIDATION",
];

const VISION_TIMELINE = [
  "IDENTITY DRIVEN TRAVEL",
  "IDENTITY DRIVEN TOURISM",
  "SOULPRINT INTELLIGENCE",
  "ORZA INTELLIGENCE",
  "EXPERIENCE ARCHITECTURE",
  "GLOBAL TRAVEL IDENTITY NETWORK",
];

// ── Shared diagram primitives ──────────────────────────────────────────────────

function FlowNode({ label, highlight = false, large = false }) {
  return (
    <div
      className={`text-center px-6 py-3 border rounded-sm tracking-[0.18em] uppercase transition-colors ${
        highlight
          ? "border-[#C9A84C]/50 text-[#C9A84C] bg-[#C9A84C]/04"
          : "border-white/10 text-white/75"
      } ${large ? "text-sm md:text-base py-4 px-8" : "text-xs md:text-sm"}`}
    >
      {label}
    </div>
  );
}

function FlowConnector({ gold = false }) {
  return (
    <div className={`w-px mx-auto ${gold ? "h-8 bg-[#C9A84C]/50" : "h-7 bg-white/10"}`} />
  );
}

// ── Ecosystem wheel (desktop) ──────────────────────────────────────────────────

function EcosystemWheel() {
  const SIZE = 580;
  const RADIUS = 220;
  const CX = SIZE / 2;
  const CY = SIZE / 2;

  const nodePos = (angleDeg) => {
    const rad = angleDeg * (Math.PI / 180);
    return {
      left: CX + RADIUS * Math.sin(rad),
      top:  CY - RADIUS * Math.cos(rad),
    };
  };

  return (
    <div className="hidden md:block mx-auto" style={{ position: "relative", width: SIZE, height: SIZE }}>
      {/* SVG layer: outer ring + spokes */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        <circle cx={CX} cy={CY} r={RADIUS} fill="none" stroke="#C9A84C" strokeWidth="0.6" strokeOpacity="0.12" />
        {ECOSYSTEM_NODES.map((n) => {
          const p = nodePos(n.angle);
          return (
            <line key={n.label} x1={CX} y1={CY} x2={p.left} y2={p.top}
              stroke="#C9A84C" strokeWidth="0.5" strokeOpacity="0.12" />
          );
        })}
      </svg>

      {/* Center node */}
      <div style={{ position: "absolute", left: CX, top: CY, transform: "translate(-50%, -50%)" }}>
        <div className="text-center px-5 py-4 border border-[#C9A84C]/35 rounded-sm bg-[#0A0A0A]">
          <p className="text-[9px] uppercase tracking-[0.3em] text-[#C9A84C]/60 mb-0.5">at the center</p>
          <p className="font-heading text-base font-light text-[#C9A84C] tracking-wider">TRAVELORZA</p>
        </div>
      </div>

      {/* Outer nodes */}
      {ECOSYSTEM_NODES.map((n) => {
        const p = nodePos(n.angle);
        const isIdentity = n.angle === 0;
        return (
          <div key={n.label} style={{ position: "absolute", left: p.left, top: p.top, transform: "translate(-50%, -50%)" }}>
            <div
              className={`text-center px-3 py-2 border rounded-sm whitespace-nowrap ${
                isIdentity
                  ? "border-[#C9A84C]/45 text-[#C9A84C] bg-[#C9A84C]/04"
                  : "border-white/10 text-white/65 bg-[#0A0A0A]"
              }`}
            >
              <p className="text-[9px] uppercase tracking-[0.18em]">{n.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Mobile ecosystem: vertical sequential cycle — preserves sequence, evolution, transformation logic
function EcosystemMobile() {
  return (
    <div className="md:hidden max-w-xs mx-auto">
      {/* TRAVELORZA at top */}
      <div className="text-center">
        <div className="inline-block px-6 py-3 border border-[#C9A84C]/35 rounded-sm bg-[#0A0A0A]">
          <p className="text-[9px] uppercase tracking-[0.3em] text-[#C9A84C]/60 mb-0.5">at the center</p>
          <p className="font-heading text-base font-light text-[#C9A84C] tracking-wider">TRAVELORZA</p>
        </div>
      </div>

      {/* Sequential nodes — full cycle visible */}
      {ECOSYSTEM_NODES.map((node) => (
        <div key={node.label}>
          <div className="w-px h-7 bg-[#C9A84C]/35 mx-auto" />
          <div
            className={`text-center px-5 py-2.5 border rounded-sm mx-2 ${
              node.angle === 0
                ? "border-[#C9A84C]/45 text-[#C9A84C] bg-[#C9A84C]/04"
                : "border-white/8 text-white/55 bg-[#0A0A0A]"
            }`}
          >
            <p className="text-[9px] uppercase tracking-[0.18em]">{node.label}</p>
          </div>
        </div>
      ))}

      {/* Cycle return — reinforces it never ends */}
      <div className="w-px h-7 bg-[#C9A84C]/20 mx-auto" />
      <div className="text-center">
        <p className="text-[9px] uppercase tracking-[0.2em] text-[#C9A84C]/45">↺ back to identity</p>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function About() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="about-page">
      <Navbar onPlanJourney={() => setModalOpen(true)} />

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 1 — HERO
          ══════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[70vh] flex items-end justify-start overflow-hidden px-6 md:px-16 pb-16">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
        </div>
        <div className="relative z-10 max-w-3xl pt-24">
          <p className="overline mb-5">Our Philosophy</p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light text-white tracking-tight leading-none mb-6">
            We Don&apos;t Build Itineraries.
            <br />
            <span className="italic text-[#C9A84C]">We Design Transformations.</span>
          </h1>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-xl leading-relaxed">
            Travel was never meant to be a product.
          </p>
          <p className="text-[#71717A] text-base md:text-lg max-w-xl leading-relaxed mt-3">
            Yet most of the industry still sells destinations, packages and itineraries.
          </p>
          <p className="text-white/60 text-base md:text-lg max-w-xl leading-relaxed mt-3">
            We believe travel is one of the few experiences capable of changing who we become.
          </p>
          <p className="text-[#C9A84C] text-base md:text-lg max-w-xl leading-relaxed mt-4 font-medium">
            Travelorza exists to understand who you are becoming and design journeys around that transformation.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 2 — THE QUESTION THAT STARTED EVERYTHING
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          <div>
            <p className="overline mb-5">The Question That Started Everything</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white/90 leading-snug tracking-tight">
              Why do two people visit the same destination and return with completely different experiences?
            </h2>
          </div>
          <div className="space-y-6 pt-2 md:pt-16">
            <p className="text-[#A1A1AA] text-base leading-relaxed">
              That question led to years of research into identity, transformation, travel behaviour and human experience.
            </p>
            <p className="text-[#71717A] text-base leading-relaxed">
              Eventually it became the foundation of everything Travelorza builds today.
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 3 — DISCOVERY CARDS
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#111111]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          <div data-testid="vision-section" className="p-10 border border-[#C9A84C]/15 rounded-xl">
            <p className="overline mb-4 text-[#C9A84C]">IDENTITY SHAPES EXPERIENCE</p>
            <p className="text-[#A1A1AA] text-base leading-relaxed">
              People do not travel for destinations.
            </p>
            <p className="text-white/65 text-base leading-relaxed mt-3">
              They travel for what those destinations allow them to feel, become, discover or express.
            </p>
          </div>
          <div data-testid="mission-section" className="p-10 border border-white/5 rounded-xl">
            <p className="overline mb-4">EXPERIENCE SHAPES IDENTITY</p>
            <p className="text-[#A1A1AA] text-base leading-relaxed">
              Every meaningful journey changes the traveler.
            </p>
            <p className="text-white/65 text-base leading-relaxed mt-3">
              The traveler returns different from the person who departed.
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 4 — THE EVOLUTION OF AN IDEA
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline mb-4">Intellectual Foundation</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tight">
              The Evolution <span className="italic text-[#C9A84C]">of an Idea</span>
            </h2>
          </div>
          <div className="max-w-xs mx-auto">
            {EVOLUTION_STEPS.map((step, i) => (
              <div key={step}>
                <FlowNode
                  label={step}
                  highlight={i === 0 || i === EVOLUTION_STEPS.length - 1}
                  large={i === 0}
                />
                {i < EVOLUTION_STEPS.length - 1 && <FlowConnector gold />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 4.5 — HOW TRANSFORMATION HAPPENS
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <p className="overline mb-4">The Core Model</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tight leading-tight">
              How Transformation <span className="italic text-[#C9A84C]">Happens</span>
            </h2>
            <p className="text-[#71717A] text-sm mt-5 leading-relaxed max-w-sm">
              This is the most important conceptual model on this page. Everything Travelorza builds originates here.
            </p>
          </div>
          <div className="max-w-xs mx-auto">
            {TRANSFORMATION_MODEL.map((step, i) => (
              <div key={step}>
                <FlowNode label={step} highlight large />
                {i < TRANSFORMATION_MODEL.length - 1 && <FlowConnector gold />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 5 — THE TRAVELORZA ECOSYSTEM
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline mb-4">Ecosystem</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tight">
              The Travelorza <span className="italic text-[#C9A84C]">Ecosystem</span>
            </h2>
            <p className="text-[#71717A] text-sm mt-5 max-w-sm mx-auto leading-relaxed">
              Identity initiates the cycle. Transformation completes it. The cycle continues.
            </p>
          </div>
          <EcosystemWheel />
          <EcosystemMobile />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 6 — BUILT IN RESEARCH. PROVEN IN PRACTICE.
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <p className="overline mb-4">Foundations</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tight">
              Built in Research. <span className="italic text-[#C9A84C]">Proven in Practice.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 md:gap-16">
            <div className="p-8 border border-white/6 rounded-xl">
              <p className="overline mb-6 text-[#C9A84C]">Intellectual Foundation</p>
              <div className="space-y-3">
                {INTELLECTUAL_PILLARS.map(p => (
                  <div key={p} className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-[#C9A84C]/60 shrink-0" />
                    <p className="text-sm text-[#A1A1AA]">{p}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-8 border border-white/6 rounded-xl">
              <p className="overline mb-6">Operational Foundation</p>
              <div className="space-y-3">
                {OPERATIONAL_PILLARS.map(p => (
                  <div key={p} className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-white/30 shrink-0" />
                    <p className="text-sm text-[#A1A1AA]">{p}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 7 — PROOF OF EXECUTION
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          {/* Stat block — editorial left edge */}
          <div className="mb-16">
            <p className="overline mb-6">Proof of Execution</p>
            <p className="font-heading font-light text-[#C9A84C] leading-none mb-4"
              style={{ fontSize: "clamp(3rem, 8vw, 5.5rem)" }}>
              ₹1+ Crore
            </p>
            <p className="font-heading text-xl md:text-2xl font-light text-white/70 mb-6">First Year Revenue</p>
            <p className="text-[#71717A] text-sm leading-relaxed max-w-lg">
              Travelorza crossed ₹1 crore in revenue within its first year of incorporation.
            </p>
            <p className="text-[#C9A84C]/70 text-sm leading-relaxed max-w-lg mt-4 italic">
              This is not proof of success. It is proof that the philosophy resonates in the real world.
            </p>
          </div>

          {/* Validation flowchart — centered figure, same axis as Ecosystem */}
          <div className="text-center mb-4">
            <p className="text-[9px] uppercase tracking-[0.3em] text-[#71717A]">From idea to validation</p>
          </div>
          <div className="max-w-[220px] mx-auto">
            {VALIDATION_FLOW.map((step, i) => (
              <div key={step}>
                <div className={`text-center px-4 py-2.5 border rounded-sm text-xs uppercase tracking-[0.18em] ${
                  step === "VALIDATION"
                    ? "border-[#C9A84C]/40 text-[#C9A84C]"
                    : "border-white/8 text-white/55"
                }`}>
                  {step}
                </div>
                {i < VALIDATION_FLOW.length - 1 && (
                  <div className="w-px h-7 bg-[#C9A84C]/30 mx-auto" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 8 — FOUNDER MARKET FIT
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="overline mb-4">Founder Market Fit</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tight">
              The <span className="italic text-[#C9A84C]">Founders</span>
            </h2>
          </div>

          {/* Three-column: founder | equation | founder */}
          <div className="grid md:grid-cols-3 gap-8 items-center mb-14">
            {/* Evan */}
            <div data-testid="founder-card-0" className="p-8 bg-[#0A0A0A] border border-white/5 rounded-xl hover:border-white/10 transition-colors">
              <div className="flex items-start gap-4 mb-6">
                <img src={FOUNDERS[0].imageUrl} alt={FOUNDERS[0].name}
                  className="w-14 h-14 rounded-full object-cover shrink-0"
                  style={{ border: `1px solid ${FOUNDERS[0].color}30` }}
                  onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                />
                <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 font-heading text-xl font-light"
                  style={{ background: `${FOUNDERS[0].color}15`, border: `1px solid ${FOUNDERS[0].color}30`, color: FOUNDERS[0].color, display: "none" }}>
                  EV
                </div>
                <div>
                  <h3 className="font-heading text-xl font-light text-white mb-1">{FOUNDERS[0].name}</h3>
                  <p className="text-xs uppercase tracking-[0.15em]" style={{ color: FOUNDERS[0].color }}>{FOUNDERS[0].title}</p>
                </div>
              </div>
              <div className="space-y-2">
                {FOUNDERS[0].pillars.map(p => (
                  <div key={p} className="flex items-center gap-2.5">
                    <div className="w-1 h-1 rounded-full shrink-0" style={{ background: FOUNDERS[0].color + "80" }} />
                    <p className="text-xs text-[#A1A1AA]">{p}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Central equation */}
            <div className="flex flex-col items-center py-8">
              <div className="text-center space-y-3">
                {["RESEARCH", "OPERATIONS", "EXECUTION"].map((item, i) => (
                  <div key={item}>
                    <p className="text-xs uppercase tracking-[0.25em] text-white/55">{item}</p>
                    {i < 2 && <p className="text-[#C9A84C]/40 text-lg leading-none mt-1">+</p>}
                  </div>
                ))}
                <div className="w-full h-px bg-[#C9A84C]/30 my-4" />
                <div className="px-5 py-3 border border-[#C9A84C]/30 rounded-sm">
                  <p className="font-heading text-sm font-light text-[#C9A84C] tracking-widest">TRAVELORZA</p>
                </div>
              </div>
            </div>

            {/* Dawoudine */}
            <div data-testid="founder-card-1" className="p-8 bg-[#0A0A0A] border border-white/5 rounded-xl hover:border-white/10 transition-colors">
              <div className="flex items-start gap-4 mb-6">
                <img src={FOUNDERS[1].imageUrl} alt={FOUNDERS[1].name}
                  className="w-14 h-14 rounded-full object-cover shrink-0"
                  style={{ border: `1px solid ${FOUNDERS[1].color}30` }}
                  onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                />
                <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 font-heading text-xl font-light"
                  style={{ background: `${FOUNDERS[1].color}15`, border: `1px solid ${FOUNDERS[1].color}30`, color: FOUNDERS[1].color, display: "none" }}>
                  MD
                </div>
                <div>
                  <h3 className="font-heading text-xl font-light text-white mb-1">{FOUNDERS[1].name}</h3>
                  <p className="text-xs uppercase tracking-[0.15em]" style={{ color: FOUNDERS[1].color }}>{FOUNDERS[1].title}</p>
                </div>
              </div>
              <div className="space-y-2">
                {FOUNDERS[1].pillars.map(p => (
                  <div key={p} className="flex items-center gap-2.5">
                    <div className="w-1 h-1 rounded-full shrink-0" style={{ background: FOUNDERS[1].color + "80" }} />
                    <p className="text-xs text-[#A1A1AA]">{p}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Founder philosophy */}
          <div className="text-center">
            <div className="inline-block glass rounded-xl px-8 md:px-14 py-8 max-w-2xl">
              <div className="text-4xl text-[#C9A84C]/25 font-heading mb-3 leading-none">&ldquo;</div>
              <p className="font-heading text-xl md:text-2xl font-light text-white italic leading-relaxed">
                Research without execution remains an idea.<br />
                Execution without insight becomes a commodity.<br />
                Travelorza was built on both.
              </p>
              <p className="text-[9px] uppercase tracking-[0.3em] text-[#C9A84C] mt-5">
                — Dr. Evan Viswas &amp; Mr. Mohamed Dawoudine
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 9 — WHAT WE ARE BUILDING
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <p className="overline mb-5">Long-Term Vision</p>
              <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tight leading-tight mb-6">
                What We Are <span className="italic text-[#C9A84C]">Building</span>
              </h2>
              <p className="text-[#A1A1AA] text-base leading-relaxed mb-4">
                Travelorza is not simply building journeys.
              </p>
              <p className="text-[#71717A] text-base leading-relaxed mb-8">
                It is building the foundations of an Identity Driven Tourism ecosystem.
              </p>
              <p className="font-heading text-lg font-light italic text-[#C9A84C]/70 leading-relaxed max-w-sm">
                A future where travelers choose experiences not by destination, price or trends, but by who they are becoming.
              </p>
            </div>
            {/* Timeline */}
            <div>
              <div className="border-l border-[#C9A84C]/25 pl-8 space-y-0">
                {VISION_TIMELINE.map((step, i) => (
                  <div key={step} className="relative pb-8 last:pb-0">
                    <div className="absolute -left-[33px] top-0.5 w-2 h-2 rounded-full border border-[#C9A84C]/50"
                      style={{ background: i === VISION_TIMELINE.length - 1 ? "#C9A84C" : "#0A0A0A" }} />
                    <p className={`text-xs uppercase tracking-[0.2em] ${
                      i === VISION_TIMELINE.length - 1 ? "text-[#C9A84C]" : "text-white/55"
                    }`}>
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 10 — FINAL CTA
          ══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-6 text-center bg-[#111111]">
        <div className="max-w-2xl mx-auto">
          <p className="overline mb-4">The Beginning</p>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-white mb-6 tracking-tight">
            Begin with <span className="italic text-[#C9A84C]">Who You Are.</span>
          </h2>
          <p className="text-[#A1A1AA] mb-3 max-w-lg mx-auto leading-relaxed">
            The most meaningful journeys do not begin with a destination.
          </p>
          <p className="text-[#71717A] mb-10 max-w-lg mx-auto leading-relaxed">
            They begin with identity.
          </p>
          <Link to="/quiz" data-testid="about-cta-btn" className="btn-gold text-xs px-12 py-4 inline-block">
            Discover Your Soulprint
          </Link>
        </div>
      </section>

      <Footer onPlanJourney={() => setModalOpen(true)} />
      <LeadModal isOpen={modalOpen} onClose={() => setModalOpen(false)} source="about_page" />
    </div>
  );
}
